import * as Cesium from "cesium";

import { Tileset } from "@/types/project";

interface LodManagerOptions {
  tilesets: Tileset[];
  isTilesetLoaded: (tilesetId: string) => boolean;
  loadTileset: (tileset: Tileset) => Promise<void>;
  unloadTileset: (tilesetId: string) => Promise<void>;
  setTilesetVisibility: (tilesetId: string, visible: boolean) => void;
  setTilesetOpacity: (tilesetId: string, opacity: number) => void;
}

/** Durée de la transition entre LODs en ms */
const TRANSITION_DURATION = 600;

/** Marge de préchargement : charger le prochain LOD X% avant d'entrer dans sa zone */
const PRELOAD_MARGIN = 1.3;

/** Hystérésis pour éviter les allers-retours de LOD autour des seuils */
const LOD_HYSTERESIS_RATIO = 0.15;

/** Durée de conservation d'un ancien LOD en cache court (ms) */
const SHORT_LIVED_OLD_LOD_CACHE_MS = 3000;

/** Nombre maximum de grilles traitées par passe pendant un mouvement caméra */
const MAX_GRIDS_PER_MOVING_UPDATE = 4;

/** Intervalle mini entre deux passes budgetisées pendant le mouvement */
const MOVING_UPDATE_INTERVAL_MS = 16;

/** Epsilon pour éviter des écritures d'opacité inutiles */
const OPACITY_CHANGE_EPSILON = 0.01;

/**
 * Seuils de distance de référence pour répartir les LOD (en mètres).
 * Adaptés pour un projet immobilier/architectural :
 * - 500m : vue lointaine / contexte urbain
 * - 150m : vue d'ensemble du quartier
 * - 50m : vue du bâtiment
 * - 0m : vue détaillée (façades, textures)
 */
const DISTANCE_THRESHOLDS = [500, 150, 50, 0];

/**
 * Seuils de distance calculés dynamiquement pour chaque LOD.
 * Clé: niveau de LOD, Valeur: distance minimale en mètres
 */
type DynamicLodThresholds = Map<number, number>;

/**
 * Gestionnaire de LOD pour les tuiles de fond de carte.
 * Décide quel LOD afficher selon la distance caméra et le frustum.
 */
export class LodManager {
  private readonly viewer: Cesium.Viewer;
  private readonly debounceMs: number;
  private readonly tileBoundingSphereRadius: number;

  private options: LodManagerOptions | null = null;
  private debounceTimer: number | null = null;
  private isUpdating = false;
  private hasPendingUpdate = false;
  private cameraMoveEndHandler: (() => void) | null = null;
  private cameraChangedHandler: (() => void) | null = null;

  /** Timestamp du dernier update déclenché pendant le mouvement */
  private lastMovingUpdateTime = 0;
  /** Intervalle minimum entre deux updates pendant le mouvement (ms) */
  private readonly throttleMs: number;

  /** Transitions d'opacité actives par tilesetId */
  private activeTransitions = new Map<
    string,
    {
      from: number;
      to: number;
      startTime: number;
      duration: number;
    }
  >();

  /** Boucle RAF unique pour animer toutes les transitions actives */
  private transitionAnimationFrameId: number | null = null;

  /** LOD actuellement actif par clé de grille (ex: "25_28" → 2) */
  private currentLodPerGrid = new Map<string, number>();

  /** LODs préchargés par clé de grille */
  private preloadedLods = new Map<string, Set<number>>();

  /** Seuils de LOD calculés dynamiquement par groupe de grille */
  private lodThresholdsPerGrid = new Map<string, DynamicLodThresholds>();

  /** Ordre logique des LOD par grille (du moins détaillé au plus détaillé) */
  private lodOrderPerGrid = new Map<string, number[]>();

  /** LOD par défaut par groupe de grille (généralement le LOD le plus élevé/détaillé) */
  private defaultLodPerGrid = new Map<string, number>();

  /** Indique si la caméra est actuellement en mouvement */
  private isCameraMoving = false;

  /** Tuiles à décharger à la fin du mouvement */
  private pendingUnloadOnMoveEnd = new Set<string>();

  /** Timers de cache court pour conserver un ancien LOD pendant quelques secondes */
  private delayedUnloadTimers = new Map<string, number>();

  /** Ancien LOD conservé temporairement par groupe */
  private shortLivedOldLodByGrid = new Map<string, string>();

  /** Tuiles protégées contre le déchargement dans la passe courante */
  private protectedTilesets = new Set<string>();

  /** Décalage de démarrage pour distribuer le budget entre grilles */
  private movingUpdateStartIndex = 0;

  /** Timer pour replanifier une passe budgetisée pendant le mouvement */
  private movingBudgetUpdateTimer: number | null = null;

  /** Dernier état visuel appliqué, pour éviter les écritures redondantes */
  private appliedVisibilityByTilesetId = new Map<string, boolean>();
  private appliedOpacityByTilesetId = new Map<string, number>();

  constructor(
    viewer: Cesium.Viewer,
    debounceMs = 120,
    tileBoundingSphereRadius = 500,
    throttleMs = 500,
  ) {
    this.viewer = viewer;
    this.debounceMs = debounceMs;
    this.tileBoundingSphereRadius = tileBoundingSphereRadius;
    this.throttleMs = throttleMs;
  }

  /**
   * Retourne la caméra si le viewer est encore valide.
   * En HMR, le viewer peut déjà être détruit quand les callbacks de cleanup s'exécutent.
   */
  private getCameraSafely(): Cesium.Camera | null {
    try {
      if (this.viewer.isDestroyed()) {
        return null;
      }
      return this.viewer.camera;
    } catch {
      return null;
    }
  }

  /**
   * Demande un rendu seulement si le viewer est encore vivant.
   */
  private requestRenderSafely(): void {
    try {
      if (!this.viewer.isDestroyed()) {
        this.viewer.scene.requestRender();
      }
    } catch {
      // Nettoyage défensif: ignorer les accès à un viewer déjà détruit.
    }
  }

  /**
   * Connecte le gestionnaire LOD au viewer.
   */
  start(options: LodManagerOptions): void {
    this.options = options;
    this.stop();
    const camera = this.getCameraSafely();
    if (!camera) {
      console.warn(
        "[LodManager] Start ignoré: viewer détruit ou caméra indisponible",
      );
      return;
    }

    // Calculer les seuils de LOD dynamiques pour chaque groupe de tuiles
    this.computeAllLodThresholds(options.tilesets);

    // Update final précis quand la caméra s'arrête
    this.cameraMoveEndHandler = () => {
      this.isCameraMoving = false;
      if (this.movingBudgetUpdateTimer !== null) {
        window.clearTimeout(this.movingBudgetUpdateTimer);
        this.movingBudgetUpdateTimer = null;
      }
      void this.handleMoveEnd();
    };

    // Update throttlé pendant le mouvement de caméra
    this.cameraChangedHandler = () => {
      this.isCameraMoving = true;
      this.scheduleMovingUpdate();
    };

    camera.moveEnd.addEventListener(this.cameraMoveEndHandler);
    camera.changed.addEventListener(this.cameraChangedHandler);
    // Sensibilité volontairement un peu plus faible pour réduire les recalculs LOD
    // pendant les petits micro-mouvements de caméra.
    camera.percentageChanged = 0.08;

    // Première passe immédiate après connexion.
    void this.updateVisibility();
  }

  /**
   * Déconnecte le gestionnaire LOD du viewer.
   */
  stop(): void {
    if (this.debounceTimer !== null) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    const camera = this.getCameraSafely();

    if (this.cameraMoveEndHandler && camera) {
      camera.moveEnd.removeEventListener(this.cameraMoveEndHandler);
      this.cameraMoveEndHandler = null;
    } else if (this.cameraMoveEndHandler) {
      this.cameraMoveEndHandler = null;
    }

    if (this.cameraChangedHandler && camera) {
      camera.changed.removeEventListener(this.cameraChangedHandler);
      this.cameraChangedHandler = null;
    } else if (this.cameraChangedHandler) {
      this.cameraChangedHandler = null;
    }

    // Arrêter toutes les transitions
    if (this.transitionAnimationFrameId !== null) {
      window.cancelAnimationFrame(this.transitionAnimationFrameId);
      this.transitionAnimationFrameId = null;
    }
    this.activeTransitions.clear();
    this.currentLodPerGrid.clear();
    this.preloadedLods.clear();
    this.lodThresholdsPerGrid.clear();
    this.lodOrderPerGrid.clear();
    this.defaultLodPerGrid.clear();
    this.pendingUnloadOnMoveEnd.clear();
    this.shortLivedOldLodByGrid.clear();
    this.protectedTilesets.clear();
    this.isCameraMoving = false;
    this.movingUpdateStartIndex = 0;
    this.appliedVisibilityByTilesetId.clear();
    this.appliedOpacityByTilesetId.clear();

    for (const [, timerId] of this.delayedUnloadTimers) {
      window.clearTimeout(timerId);
    }
    this.delayedUnloadTimers.clear();

    if (this.movingBudgetUpdateTimer !== null) {
      window.clearTimeout(this.movingBudgetUpdateTimer);
      this.movingBudgetUpdateTimer = null;
    }
  }

  /**
   * Met à jour la visibilité des tuiles selon la position caméra.
   */
  async updateVisibility(cameraPosition?: Cesium.Cartesian3): Promise<void> {
    if (!this.options) {
      return;
    }

    const camera = this.getCameraSafely();
    if (!camera) {
      return;
    }

    if (this.isUpdating) {
      this.hasPendingUpdate = true;
      return;
    }

    this.isUpdating = true;

    try {
      const groups = this.groupTilesetsByGrid(this.options.tilesets);
      const groupEntries = Array.from(groups.entries());
      const nextProtectedTilesets = new Set<string>();
      const currentCameraPosition = cameraPosition ?? camera.positionWC;

      const cullingVolume = camera.frustum.computeCullingVolume(
        camera.positionWC,
        camera.directionWC,
        camera.upWC,
      );

      let groupsInFrustum = 0;
      let groupsOutOfFrustum = 0;
      let processedMovingGrids = 0;
      let movingBudgetExceeded = false;
      const movingPass =
        this.isCameraMoving &&
        groupEntries.length > MAX_GRIDS_PER_MOVING_UPDATE;
      const startIndex =
        movingPass && groupEntries.length > 0
          ? this.movingUpdateStartIndex % groupEntries.length
          : 0;

      for (let idx = 0; idx < groupEntries.length; idx += 1) {
        const physicalIndex = (startIndex + idx) % groupEntries.length;
        if (
          this.isCameraMoving &&
          processedMovingGrids >= MAX_GRIDS_PER_MOVING_UPDATE
        ) {
          movingBudgetExceeded = true;
          this.movingUpdateStartIndex = physicalIndex;
          break;
        }
        processedMovingGrids += 1;
        const [gridKey, tilesets] = groupEntries[physicalIndex];
        const georeferencedTileset = tilesets.find((t) => t.georef);
        if (!georeferencedTileset?.georef) continue;

        const tilePosition = Cesium.Cartesian3.fromDegrees(
          georeferencedTileset.georef.east,
          georeferencedTileset.georef.north,
          georeferencedTileset.georef.height,
        );

        const distance = Cesium.Cartesian3.distance(
          currentCameraPosition,
          tilePosition,
        );
        const lodToLoad = this.resolveTargetLod(distance, tilesets, gridKey);
        const nextLod = this.resolveTargetLod(
          distance / PRELOAD_MARGIN,
          tilesets,
          gridKey,
        );

        // Pour les LOD proches (LOD élevés = détaillés), désactiver le culling frustum car il est trop agressif
        const defaultLod = this.defaultLodPerGrid.get(gridKey) ?? 3;
        const shouldCheckFrustum = lodToLoad < defaultLod - 1;
        const isInFrustum = shouldCheckFrustum
          ? this.isInsideFrustum(tilePosition, cullingVolume)
          : true;

        if (!isInFrustum) {
          groupsOutOfFrustum++;
          this.queueGroupForUnloadAtMoveEnd(tilesets);
          this.releaseShortLivedOldLod(gridKey);
          // Réinitialiser le suivi LOD pour ce groupe (permettra une transition propre au retour)
          this.currentLodPerGrid.delete(gridKey);
          continue;
        }

        groupsInFrustum++;

        // Collecter les opérations à effectuer (pas await dans la boucle pour éviter les race conditions)
        const toLoad: Tileset[] = [];
        let unloadCount = 0;

        // Collecter les opérations de chargement/déchargement
        const targetTileset = tilesets.find((t) => t.lod === lodToLoad);
        const nextTileset = tilesets.find((t) => t.lod === nextLod);
        const defaultTileset = tilesets.find((t) => t.lod === defaultLod);
        const hasAnyLoadedLod = tilesets.some((t) =>
          this.options!.isTilesetLoaded(t.id),
        );
        const protectedTilesetsForGrid = new Set<string>();
        if (targetTileset) protectedTilesetsForGrid.add(targetTileset.id);
        if (nextTileset) protectedTilesetsForGrid.add(nextTileset.id);
        if (defaultTileset) protectedTilesetsForGrid.add(defaultTileset.id);

        // Conserver un ancien LOD en cache court pour fluidifier un retour arrière rapide.
        const previousLod = this.currentLodPerGrid.get(gridKey);
        const previousTileset =
          previousLod === undefined
            ? undefined
            : tilesets.find(
                (tileset) =>
                  tileset.lod === previousLod &&
                  this.options!.isTilesetLoaded(tileset.id),
              );
        const previousCachedTilesetId =
          this.shortLivedOldLodByGrid.get(gridKey);

        let shortLivedOldLodId: string | null = null;
        if (
          previousTileset &&
          !protectedTilesetsForGrid.has(previousTileset.id)
        ) {
          shortLivedOldLodId = previousTileset.id;
          this.shortLivedOldLodByGrid.set(gridKey, shortLivedOldLodId);
          this.scheduleShortLivedOldLodUnload(shortLivedOldLodId);
          protectedTilesetsForGrid.add(shortLivedOldLodId);
        } else {
          this.releaseShortLivedOldLod(gridKey);
        }

        if (
          previousCachedTilesetId &&
          previousCachedTilesetId !== shortLivedOldLodId
        ) {
          const previousCacheTimer = this.delayedUnloadTimers.get(
            previousCachedTilesetId,
          );
          if (previousCacheTimer !== undefined) {
            window.clearTimeout(previousCacheTimer);
            this.delayedUnloadTimers.delete(previousCachedTilesetId);
          }
          this.queueUnloadAtMoveEnd(previousCachedTilesetId);
        }

        for (const protectedTilesetId of protectedTilesetsForGrid) {
          this.cancelScheduledUnload(protectedTilesetId);
          nextProtectedTilesets.add(protectedTilesetId);
        }

        // 1. Charger le LOD cible s'il existe et n'est pas déjà chargé
        if (targetTileset && !this.options.isTilesetLoaded(targetTileset.id)) {
          toLoad.push(targetTileset);
        }

        // 2. Précharger le prochain LOD anticipé (si différent du cible)
        if (
          nextTileset &&
          nextLod !== lodToLoad &&
          !this.options.isTilesetLoaded(nextTileset.id)
        ) {
          toLoad.push(nextTileset);
          // Marquer comme préchargé
          if (!this.preloadedLods.has(gridKey)) {
            this.preloadedLods.set(gridKey, new Set());
          }
          this.preloadedLods.get(gridKey)!.add(nextLod);
        }

        // 3. Charger le LOD par défaut uniquement s'il n'y a encore aucun LOD en mémoire pour cette grille.
        // Cela évite de charger systématiquement le LOD le plus détaillé (coûteux) pendant les transitions.
        if (
          !hasAnyLoadedLod &&
          defaultTileset &&
          !this.options.isTilesetLoaded(defaultTileset.id)
        ) {
          toLoad.push(defaultTileset);
        }

        // 4. Décharger les LODs qui ne sont ni cible, ni prochain, ni défaut (à la fin du mouvement).
        for (const tileset of tilesets) {
          const isLoaded = this.options.isTilesetLoaded(tileset.id);
          const isProtected = protectedTilesetsForGrid.has(tileset.id);

          if (isLoaded && !isProtected) {
            this.queueUnloadAtMoveEnd(tileset.id);
            unloadCount += 1;
          }
        }

        // Lancer les chargements en arrière-plan (non-bloquant)
        // Les tilesets apparaîtront progressivement quand ils seront prêts
        if (toLoad.length > 0) {
          // Charger en parallèle sans bloquer
          for (const tileset of toLoad) {
            this.options!.loadTileset(tileset)
              .then(() => {
                // Quand un tileset finit de charger, vérifier s'il doit être visible
                if (!this.options) return;
                const currentTargetLod = this.currentLodPerGrid.get(gridKey);
                if (currentTargetLod === tileset.lod) {
                  // Ce tileset est maintenant le LOD actif, faire une transition douce
                  this.handleLodTransitionAfterLoad(
                    gridKey,
                    tilesets,
                    tileset.lod,
                  );
                } else {
                  // Ce tileset a été préchargé mais n'est pas encore actif, le cacher
                  this.setTilesetVisibilityIfChanged(
                    `tileset-${tileset.id}`,
                    false,
                  );
                }
              })
              .catch((err) =>
                console.error(
                  `[LodManager] Erreur chargement ${tileset.id}:`,
                  err,
                ),
              );
          }
        }

        // 5. Gérer la visibilité avec transition fluide
        this.handleLodTransition(
          gridKey,
          tilesets,
          targetTileset,
          defaultTileset,
          lodToLoad,
        );
      }

      if (movingBudgetExceeded) {
        this.protectedTilesets = new Set([
          ...this.protectedTilesets,
          ...nextProtectedTilesets,
        ]);
      } else {
        this.protectedTilesets = nextProtectedTilesets;
        this.movingUpdateStartIndex = 0;
      }

      if (movingBudgetExceeded) {
        this.scheduleMovingBudgetedUpdate();
      }
    } finally {
      this.requestRenderSafely();
      this.isUpdating = false;

      // Ne décharge jamais pendant un mouvement caméra.
      if (!this.isCameraMoving) {
        void this.flushPendingUnloads();
      }

      if (this.hasPendingUpdate) {
        this.hasPendingUpdate = false;
        void this.updateVisibility();
      }
    }
  }

  private async handleMoveEnd(): Promise<void> {
    try {
      await this.scheduleUpdate();
      await this.flushPendingUnloads();
    } catch (error) {
      console.error("[LodManager] Erreur handleMoveEnd:", error);
    }
  }

  private scheduleUpdate(): Promise<void> {
    if (this.debounceTimer !== null) {
      window.clearTimeout(this.debounceTimer);
    }

    return new Promise((resolve) => {
      this.debounceTimer = window.setTimeout(() => {
        this.debounceTimer = null;
        void this.updateVisibility().finally(resolve);
      }, this.debounceMs);
    });
  }

  /**
   * Planifie un update pendant le mouvement avec throttle.
   * Contrairement au debounce, le throttle garantit une exécution régulière.
   */
  private scheduleMovingUpdate(): void {
    const now = Date.now();
    if (now - this.lastMovingUpdateTime < this.throttleMs) return;

    this.lastMovingUpdateTime = now;
    void this.updateVisibility();
  }

  private scheduleMovingBudgetedUpdate(): void {
    if (!this.isCameraMoving) {
      return;
    }
    if (this.movingBudgetUpdateTimer !== null) {
      return;
    }

    this.movingBudgetUpdateTimer = window.setTimeout(() => {
      this.movingBudgetUpdateTimer = null;
      if (!this.isCameraMoving) {
        return;
      }
      void this.updateVisibility();
    }, MOVING_UPDATE_INTERVAL_MS);
  }

  private groupTilesetsByGrid(tilesets: Tileset[]): Map<string, Tileset[]> {
    const groups = new Map<string, Tileset[]>();

    for (const tileset of tilesets) {
      const key = `${tileset.gridColumn}_${tileset.gridRow}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(tileset);
    }

    return groups;
  }

  private isInsideFrustum(
    position: Cesium.Cartesian3,
    cullingVolume: Cesium.CullingVolume,
  ): boolean {
    const boundingSphere = new Cesium.BoundingSphere(
      position,
      this.tileBoundingSphereRadius,
    );
    return (
      cullingVolume.computeVisibility(boundingSphere) !==
      Cesium.Intersect.OUTSIDE
    );
  }

  /**
   * Détermine le LOD cible en fonction de la distance et des seuils dynamiques.
   * Utilise les seuils calculés pour ce groupe de grille spécifique.
   */
  private resolveTargetLod(
    distance: number,
    tilesets: Tileset[],
    gridKey?: string,
  ): number {
    if (!gridKey) {
      console.warn(
        "[LodManager] resolveTargetLod appelé sans gridKey, fallback sur LOD le plus proche",
      );
      return tilesets[0]?.lod ?? 0;
    }

    const thresholds = this.lodThresholdsPerGrid.get(gridKey);
    if (!thresholds) {
      console.warn(
        `[LodManager] Pas de seuils calculés pour grid ${gridKey}, recalcul`,
      );
      this.computeLodThresholds(gridKey, tilesets);
      return this.resolveTargetLod(distance, tilesets, gridKey);
    }

    // Trouver le LOD approprié selon la distance.
    // On utilise l'ordre logique (moins détaillé -> plus détaillé), pas l'ordre numérique brut.
    const availableLods =
      this.lodOrderPerGrid.get(gridKey) ??
      Array.from(thresholds.keys()).sort((a, b) => a - b);

    let targetLod = availableLods[availableLods.length - 1];

    for (const lod of availableLods) {
      const minDistance = thresholds.get(lod) ?? 0;
      if (distance >= minDistance) {
        targetLod = lod;
        break;
      }
    }

    targetLod = this.applyLodHysteresis(
      distance,
      gridKey,
      targetLod,
      availableLods,
      thresholds,
    );

    // Vérifier que ce LOD existe vraiment dans les tilesets
    const exact = tilesets.find((tileset) => tileset.lod === targetLod);
    if (exact) return exact.lod;

    // Fallback: prendre le LOD disponible le plus proche de la cible.
    return [...tilesets].sort(
      (a, b) => Math.abs(a.lod - targetLod) - Math.abs(b.lod - targetLod),
    )[0].lod;
  }

  private queueGroupForUnloadAtMoveEnd(tilesets: Tileset[]): void {
    for (const tileset of tilesets) {
      this.queueUnloadAtMoveEnd(tileset.id);
    }
  }

  private applyLodHysteresis(
    distance: number,
    gridKey: string,
    proposedLod: number,
    availableLods: number[],
    thresholds: DynamicLodThresholds,
  ): number {
    const currentLod = this.currentLodPerGrid.get(gridKey);
    if (
      currentLod === undefined ||
      currentLod === proposedLod ||
      !availableLods.includes(currentLod)
    ) {
      return proposedLod;
    }

    const currentLodIndex = availableLods.indexOf(currentLod);
    const proposedLodIndex = availableLods.indexOf(proposedLod);
    if (currentLodIndex === -1 || proposedLodIndex === -1) {
      return proposedLod;
    }

    // Zoom avant: ne monte en détail que si on dépasse franchement le seuil.
    if (proposedLodIndex > currentLodIndex) {
      const currentThreshold = thresholds.get(currentLod);
      if (currentThreshold === undefined) {
        return proposedLod;
      }
      const enterDetailedDistance =
        currentThreshold * (1 - LOD_HYSTERESIS_RATIO);
      return distance <= enterDetailedDistance ? proposedLod : currentLod;
    }

    // Zoom arrière: ne redescend en détail que si on s'éloigne assez.
    const adjacentLowerLod = availableLods[currentLodIndex - 1];
    if (adjacentLowerLod === undefined) {
      return proposedLod;
    }

    const lowerThreshold = thresholds.get(adjacentLowerLod) ?? 0;
    const leaveDetailedDistance = lowerThreshold * (1 + LOD_HYSTERESIS_RATIO);
    return distance >= leaveDetailedDistance ? proposedLod : currentLod;
  }

  private queueUnloadAtMoveEnd(tilesetId: string): void {
    if (this.delayedUnloadTimers.has(tilesetId)) {
      return;
    }
    this.pendingUnloadOnMoveEnd.add(tilesetId);
  }

  private cancelScheduledUnload(tilesetId: string): void {
    const timerId = this.delayedUnloadTimers.get(tilesetId);
    if (timerId !== undefined) {
      window.clearTimeout(timerId);
      this.delayedUnloadTimers.delete(tilesetId);
    }
    this.pendingUnloadOnMoveEnd.delete(tilesetId);
  }

  private scheduleShortLivedOldLodUnload(tilesetId: string): void {
    this.pendingUnloadOnMoveEnd.delete(tilesetId);
    if (this.delayedUnloadTimers.has(tilesetId)) {
      return;
    }

    const timerId = window.setTimeout(() => {
      this.delayedUnloadTimers.delete(tilesetId);
      this.pendingUnloadOnMoveEnd.add(tilesetId);

      if (!this.isCameraMoving) {
        void this.flushPendingUnloads();
      }
    }, SHORT_LIVED_OLD_LOD_CACHE_MS);

    this.delayedUnloadTimers.set(tilesetId, timerId);
  }

  private releaseShortLivedOldLod(gridKey: string): void {
    const cachedTilesetId = this.shortLivedOldLodByGrid.get(gridKey);
    if (!cachedTilesetId) {
      return;
    }
    this.shortLivedOldLodByGrid.delete(gridKey);
    const timerId = this.delayedUnloadTimers.get(cachedTilesetId);
    if (timerId !== undefined) {
      window.clearTimeout(timerId);
      this.delayedUnloadTimers.delete(cachedTilesetId);
    }
    this.queueUnloadAtMoveEnd(cachedTilesetId);
  }

  private async flushPendingUnloads(): Promise<void> {
    if (!this.options || this.isCameraMoving) {
      return;
    }

    const tilesetIds = Array.from(this.pendingUnloadOnMoveEnd);
    this.pendingUnloadOnMoveEnd.clear();

    await Promise.all(
      tilesetIds.map((tilesetId) => this.unloadTilesetIfEligible(tilesetId)),
    );
  }

  private async unloadTilesetIfEligible(tilesetId: string): Promise<void> {
    if (!this.options) {
      return;
    }
    if (this.protectedTilesets.has(tilesetId)) {
      return;
    }
    if (this.delayedUnloadTimers.has(tilesetId)) {
      return;
    }
    if (!this.options.isTilesetLoaded(tilesetId)) {
      return;
    }

    try {
      await this.options.unloadTileset(tilesetId);
      this.forgetTilesetVisualState(`tileset-${tilesetId}`);
    } catch (error) {
      console.error(`[LodManager] Erreur déchargement ${tilesetId}:`, error);
    }
  }

  private setTilesetVisibilityIfChanged(
    modelTilesetId: string,
    visible: boolean,
  ): void {
    if (!this.options) return;
    const previous = this.appliedVisibilityByTilesetId.get(modelTilesetId);
    if (previous === visible) {
      return;
    }
    this.options.setTilesetVisibility(modelTilesetId, visible);
    this.appliedVisibilityByTilesetId.set(modelTilesetId, visible);
  }

  private setTilesetOpacityIfChanged(
    modelTilesetId: string,
    opacity: number,
  ): void {
    if (!this.options) return;
    const clampedOpacity = Math.max(0, Math.min(1, opacity));
    const previous = this.appliedOpacityByTilesetId.get(modelTilesetId);
    if (
      previous !== undefined &&
      Math.abs(previous - clampedOpacity) < OPACITY_CHANGE_EPSILON
    ) {
      return;
    }
    this.options.setTilesetOpacity(modelTilesetId, clampedOpacity);
    this.appliedOpacityByTilesetId.set(modelTilesetId, clampedOpacity);
  }

  private setTilesetStateIfChanged(
    modelTilesetId: string,
    visible: boolean,
    opacity: number,
  ): void {
    this.setTilesetVisibilityIfChanged(modelTilesetId, visible);
    this.setTilesetOpacityIfChanged(modelTilesetId, opacity);
  }

  private forgetTilesetVisualState(modelTilesetId: string): void {
    this.appliedVisibilityByTilesetId.delete(modelTilesetId);
    this.appliedOpacityByTilesetId.delete(modelTilesetId);
  }

  private applyInstantLodVisibility(
    tilesets: Tileset[],
    targetTileset: Tileset | undefined,
    defaultTileset: Tileset | undefined,
    previousTileset: Tileset | undefined,
  ): void {
    if (!this.options) return;

    let visibleTileset: Tileset | undefined;
    if (targetTileset && this.options.isTilesetLoaded(targetTileset.id)) {
      visibleTileset = targetTileset;
    } else if (
      previousTileset &&
      this.options.isTilesetLoaded(previousTileset.id)
    ) {
      visibleTileset = previousTileset;
    } else if (
      defaultTileset &&
      this.options.isTilesetLoaded(defaultTileset.id)
    ) {
      visibleTileset = defaultTileset;
    }

    for (const tileset of tilesets) {
      if (!this.options.isTilesetLoaded(tileset.id)) continue;
      const modelTilesetId = `tileset-${tileset.id}`;
      if (visibleTileset && visibleTileset.id === tileset.id) {
        this.setTilesetStateIfChanged(modelTilesetId, true, 1);
      } else {
        this.setTilesetVisibilityIfChanged(modelTilesetId, false);
      }
    }
  }

  /**
   * Gère la transition fluide entre LODs avec interpolation d'opacité.
   * Le nouveau LOD fait un fondu entrant tandis que l'ancien fait un fondu sortant.
   */
  private handleLodTransition(
    gridKey: string,
    tilesets: Tileset[],
    targetTileset: Tileset | undefined,
    defaultTileset: Tileset | undefined,
    targetLod: number,
  ): void {
    if (!this.options) return;

    const previousLod = this.currentLodPerGrid.get(gridKey);
    const defaultLod = this.defaultLodPerGrid.get(gridKey) ?? 3;

    let previousTileset: Tileset | undefined;
    if (previousLod !== undefined) {
      previousTileset = tilesets.find(
        (t) => t.lod === previousLod && this.options!.isTilesetLoaded(t.id),
      );
    }

    const targetLoaded = Boolean(
      targetTileset && this.options.isTilesetLoaded(targetTileset.id),
    );
    const defaultLoaded = Boolean(
      defaultTileset && this.options.isTilesetLoaded(defaultTileset.id),
    );

    // Pendant un mouvement caméra, éviter les transitions rAF coûteuses.
    if (this.isCameraMoving) {
      if (previousLod !== targetLod) {
        this.currentLodPerGrid.set(gridKey, targetLod);
      }
      this.applyInstantLodVisibility(
        tilesets,
        targetTileset,
        defaultTileset,
        previousTileset,
      );
      this.preloadedLods.get(gridKey)?.delete(targetLod);
      return;
    }

    // Si le LOD n'a pas changé, s'assurer que la cible est visible à pleine opacité
    // sans relancer de transition (évite le clignotement)
    if (previousLod === targetLod) {
      if (targetTileset && targetLoaded) {
        const tilesetId = `tileset-${targetTileset.id}`;
        // Seulement forcer l'opacité si aucune transition active pour ce tileset
        if (!this.activeTransitions.has(tilesetId)) {
          this.setTilesetStateIfChanged(tilesetId, true, 1);
        }
      }
      // Cacher les LODs préchargés qui ne sont pas le target ou le default
      for (const tileset of tilesets) {
        if (
          tileset.lod !== targetLod &&
          tileset.lod !== defaultLod &&
          this.options.isTilesetLoaded(tileset.id)
        ) {
          this.setTilesetVisibilityIfChanged(`tileset-${tileset.id}`, false);
        }
      }
      return;
    }

    // Le LOD a changé, mettre à jour le suivi
    this.currentLodPerGrid.set(gridKey, targetLod);

    // Cas 1: Transition depuis l'ancien LOD vers le nouveau
    if (
      targetTileset &&
      targetLoaded &&
      previousTileset &&
      previousTileset.id !== targetTileset.id
    ) {
      this.transitionOpacity(
        `tileset-${targetTileset.id}`,
        0,
        1,
        TRANSITION_DURATION,
      );
      this.transitionOpacity(
        `tileset-${previousTileset.id}`,
        1,
        0,
        TRANSITION_DURATION,
      );
      return;
    }

    // Cas 2: On est sur le LOD par défaut (transition douce)
    if (targetLod === defaultLod && defaultTileset && defaultLoaded) {
      // Si un tileset était visible avant, faire une transition
      if (previousTileset && previousTileset.lod !== defaultLod) {
        this.transitionOpacity(
          `tileset-${defaultTileset.id}`,
          0,
          1,
          TRANSITION_DURATION,
        );
        this.transitionOpacity(
          `tileset-${previousTileset.id}`,
          1,
          0,
          TRANSITION_DURATION,
        );
      } else {
        // Sinon afficher directement
        this.setTilesetStateIfChanged(`tileset-${defaultTileset.id}`, true, 1);
      }
      return;
    }

    // Cas 3: Le LOD cible est déjà chargé (potentiellement préchargé)
    if (targetTileset && targetLoaded) {
      // Vérifier s'il était préchargé (donc invisible)
      const wasPreloaded = this.preloadedLods.get(gridKey)?.has(targetLod);
      const tilesetId = `tileset-${targetTileset.id}`;

      if (wasPreloaded || !this.activeTransitions.has(tilesetId)) {
        // Faire une transition douce depuis le tileset actuellement visible
        const currentVisible = tilesets.find(
          (t) =>
            this.options!.isTilesetLoaded(t.id) &&
            t.id !== targetTileset.id &&
            (previousLod === undefined ||
              t.lod === previousLod ||
              t.lod === defaultLod),
        );

        if (currentVisible) {
          this.transitionOpacity(tilesetId, 0, 1, TRANSITION_DURATION);
          this.transitionOpacity(
            `tileset-${currentVisible.id}`,
            1,
            0,
            TRANSITION_DURATION,
          );
        } else {
          // Pas de tileset visible actuellement, faire un fade-in simple
          this.transitionOpacity(tilesetId, 0, 1, TRANSITION_DURATION / 2);
        }
      }
      // Nettoyer le marqueur de préchargement
      this.preloadedLods.get(gridKey)?.delete(targetLod);
      return;
    }

    // Cas 4: Le LOD cible n'est pas encore chargé, afficher le défaut en attendant
    // Le nouveau LOD apparaîtra automatiquement via handleLodTransitionAfterLoad
    if (!targetLoaded && defaultTileset && defaultLoaded) {
      const defaultId = `tileset-${defaultTileset.id}`;
      if (!this.activeTransitions.has(defaultId)) {
        // Transition douce si un autre LOD était visible
        if (previousTileset && previousTileset.lod !== defaultLod) {
          this.transitionOpacity(defaultId, 0, 1, TRANSITION_DURATION / 2);
          this.transitionOpacity(
            `tileset-${previousTileset.id}`,
            1,
            0,
            TRANSITION_DURATION / 2,
          );
        } else {
          this.transitionOpacity(defaultId, 0, 1, TRANSITION_DURATION / 2);
        }
      }
    }

    // Masquer les autres LODs qui ne sont ni target ni default
    for (const tileset of tilesets) {
      const isTarget = tileset.lod === targetLod;
      const isDefault = tileset.lod === defaultLod;
      if (!isTarget && !isDefault && this.options.isTilesetLoaded(tileset.id)) {
        this.setTilesetVisibilityIfChanged(`tileset-${tileset.id}`, false);
      }
    }
  }

  /**
   * Effectue une transition d'opacité entre deux valeurs.
   * Utilise requestAnimationFrame pour une animation fluide synchronisée avec le rafraîchissement de l'écran.
   */
  private transitionOpacity(
    tilesetId: string,
    from: number,
    to: number,
    duration: number,
  ): void {
    if (!this.options) return;

    if (this.isCameraMoving) {
      if (to === 0) {
        this.setTilesetStateIfChanged(tilesetId, false, 1);
      } else {
        this.setTilesetStateIfChanged(tilesetId, true, to);
      }
      return;
    }

    if (from === 0) {
      this.setTilesetStateIfChanged(tilesetId, true, 0);
    }

    this.activeTransitions.set(tilesetId, {
      from,
      to,
      startTime: performance.now(),
      duration,
    });
    this.ensureTransitionLoop();
  }

  private ensureTransitionLoop(): void {
    if (this.transitionAnimationFrameId !== null) {
      return;
    }

    this.transitionAnimationFrameId = window.requestAnimationFrame(
      this.runTransitionLoop,
    );
  }

  private runTransitionLoop = (currentTime: number): void => {
    if (!this.options) {
      this.transitionAnimationFrameId = null;
      this.activeTransitions.clear();
      return;
    }

    let hasActiveTransition = false;

    for (const [tilesetId, transition] of this.activeTransitions.entries()) {
      const elapsed = currentTime - transition.startTime;
      const progress = Math.min(elapsed / transition.duration, 1);
      const easeProgress =
        progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const opacity =
        transition.from + (transition.to - transition.from) * easeProgress;

      this.setTilesetOpacityIfChanged(tilesetId, opacity);

      if (progress < 1) {
        hasActiveTransition = true;
        continue;
      }

      this.activeTransitions.delete(tilesetId);
      if (transition.to === 0) {
        this.setTilesetStateIfChanged(tilesetId, false, 1);
      } else {
        this.setTilesetStateIfChanged(tilesetId, true, transition.to);
      }
    }

    this.requestRenderSafely();

    if (hasActiveTransition && this.activeTransitions.size > 0) {
      this.transitionAnimationFrameId = window.requestAnimationFrame(
        this.runTransitionLoop,
      );
      return;
    }

    this.transitionAnimationFrameId = null;
  };

  /**
   * Gère la transition LOD après qu'un tileset ait fini de charger.
   * Appelé en callback asynchrone quand le chargement se termine.
   */
  private handleLodTransitionAfterLoad(
    gridKey: string,
    tilesets: Tileset[],
    loadedLod: number,
  ): void {
    if (!this.options) return;

    const loadedTileset = tilesets.find((t) => t.lod === loadedLod);
    if (!loadedTileset || !this.options.isTilesetLoaded(loadedTileset.id)) {
      return;
    }

    const loadedTilesetId = `tileset-${loadedTileset.id}`;

    // Vérifier si ce LOD est toujours le LOD cible pour ce groupe
    const currentTargetLod = this.currentLodPerGrid.get(gridKey);
    if (currentTargetLod !== loadedLod) {
      // Ce tileset n'est plus le LOD actif (la caméra a bougé), le garder caché
      this.setTilesetVisibilityIfChanged(loadedTilesetId, false);
      return;
    }

    if (this.isCameraMoving) {
      this.applyInstantLodVisibility(
        tilesets,
        loadedTileset,
        undefined,
        undefined,
      );
      return;
    }

    // Trouver tous les tilesets actuellement visibles pour ce groupe (différents du nouveau)
    const visibleTilesets = tilesets.filter(
      (t) => t.id !== loadedTileset.id && this.options!.isTilesetLoaded(t.id),
    );

    if (visibleTilesets.length > 0) {
      // Transition douce depuis les tilesets visibles vers le nouveau
      this.transitionOpacity(loadedTilesetId, 0, 1, TRANSITION_DURATION);
      for (const visibleTileset of visibleTilesets) {
        this.transitionOpacity(
          `tileset-${visibleTileset.id}`,
          1,
          0,
          TRANSITION_DURATION,
        );
      }
    } else {
      // Aucun tileset visible, juste faire un fade-in
      this.transitionOpacity(loadedTilesetId, 0, 1, TRANSITION_DURATION / 2);
    }

    // Nettoyer le marqueur de préchargement
    this.preloadedLods.get(gridKey)?.delete(loadedLod);
  }

  /**
   * Calcule les seuils de distance dynamiques pour tous les groupes de grille.
   */
  private computeAllLodThresholds(tilesets: Tileset[]): void {
    const groups = this.groupTilesetsByGrid(tilesets);

    for (const [gridKey, gridTilesets] of groups.entries()) {
      this.computeLodThresholds(gridKey, gridTilesets);
    }
  }

  /**
   * Calcule les seuils de distance pour un groupe de grille spécifique.
   * Détecte les LOD disponibles et répartit les distances de référence sur ces LOD.
   *
   * Exemple: Si on a LOD 0-6 (7 niveaux) et les seuils [5000, 2000, 800, 0],
   * le système va interpoler pour créer 7 seuils répartis uniformément.
   */
  private computeLodThresholds(gridKey: string, tilesets: Tileset[]): void {
    // Détecter les LOD disponibles dans un ordre logique robuste.
    // Heuristique:
    // 1) base numérique ascendante
    // 2) inversion automatique si les fichiers suggèrent l'ordre opposé
    const numericAscendingLods = [...new Set(tilesets.map((t) => t.lod))].sort(
      (a, b) => a - b,
    );
    const availableLods = this.resolveLogicalLodOrder(
      gridKey,
      tilesets,
      numericAscendingLods,
    );

    if (availableLods.length === 0) {
      console.warn(`[LodManager] Aucun LOD disponible pour grid ${gridKey}`);
      return;
    }

    const maxLod = availableLods[availableLods.length - 1];
    const lodCount = availableLods.length;

    // Le LOD par défaut est le plus détaillé (généralement le plus élevé)
    this.defaultLodPerGrid.set(gridKey, maxLod);
    this.lodOrderPerGrid.set(gridKey, availableLods);

    // Calculer les seuils dynamiques
    const thresholds = new Map<number, number>();

    if (lodCount === 1) {
      // Un seul LOD disponible, seuil à 0
      thresholds.set(availableLods[0], 0);
    } else {
      // Répartir les seuils de référence sur les LOD disponibles
      // Les seuils de référence définissent les "points clés" de distance
      const referenceDistances = DISTANCE_THRESHOLDS.filter(
        (d, i, arr) => i === 0 || d !== arr[i - 1],
      ).sort((a, b) => b - a); // Trier décroissant

      for (let i = 0; i < availableLods.length; i++) {
        const lod = availableLods[i];

        // Interpolation linéaire entre les seuils de référence
        // LOD 0 (le plus lointain) → distance max
        // LOD max (le plus proche) → distance min (0)
        const ratio = i / (lodCount - 1);
        const maxDist = referenceDistances[0];
        const minDist = 0;

        // Interpolation inversée car LOD croissant = distance décroissante
        const distance = maxDist - ratio * (maxDist - minDist);

        thresholds.set(lod, Math.round(distance));
      }
    }

    this.lodThresholdsPerGrid.set(gridKey, thresholds);
  }

  private resolveLogicalLodOrder(
    _gridKey: string,
    tilesets: Tileset[],
    numericAscendingLods: number[],
  ): number[] {
    if (numericAscendingLods.length <= 1) {
      return numericAscendingLods;
    }

    const averageSizeByLod = new Map<number, number>();
    for (const lod of numericAscendingLods) {
      const sameLod = tilesets.filter((tileset) => tileset.lod === lod);
      const sized = sameLod
        .map((tileset) => tileset.asset?.fileSize ?? 0)
        .filter((size) => Number.isFinite(size) && size > 0);

      if (sized.length === 0) continue;

      const total = sized.reduce((sum, size) => sum + size, 0);
      averageSizeByLod.set(lod, total / sized.length);
    }

    if (averageSizeByLod.size < 2) {
      return numericAscendingLods;
    }

    // Si on a des tailles exploitables, on ordonne directement les LOD
    // du moins détaillé (fichiers plus légers) au plus détaillé (plus lourds).
    const lodsWithSize = numericAscendingLods.filter((lod) =>
      averageSizeByLod.has(lod),
    );
    if (lodsWithSize.length >= 2) {
      const orderedBySize = [...numericAscendingLods].sort((a, b) => {
        const sizeA = averageSizeByLod.get(a);
        const sizeB = averageSizeByLod.get(b);

        // Les LOD sans taille mesurée passent après ceux mesurés.
        if (sizeA === undefined && sizeB === undefined) return a - b;
        if (sizeA === undefined) return 1;
        if (sizeB === undefined) return -1;

        // Plus léger -> plus lourd (moins détaillé -> plus détaillé)
        if (sizeA !== sizeB) return sizeA - sizeB;
        return a - b;
      });

      return orderedBySize;
    }

    return numericAscendingLods;
  }
}
